import { useState, useEffect, useCallback } from 'react'
import { patternPipelineApi, type PatternPromotionResponse } from '@/services/api/patternPipeline'
import { Card, Button, LoadingSpinner, Badge } from '@/components/common'
import {
  ArrowUpRight,
  XCircle,
  RefreshCw,
  Eye,
  Filter,
} from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  stage1_passed: 'bg-blue-100 text-blue-800',
  stage2_passed: 'bg-indigo-100 text-indigo-800',
  review_queue: 'bg-purple-100 text-purple-800',
  promoted_regional: 'bg-green-100 text-green-800',
  promoted_global: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  flagged: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-gray-100 text-gray-800',
}

const FIELD_LABELS: Record<string, string> = {
  room: 'Room',
  class_name: 'Class',
  subject: 'Subject',
  teacher_name: 'Teacher Name',
  teacher_code: 'Teacher Code',
  first_name: 'First Name',
  family_name: 'Family Name',
  time_slot: 'Time Slot',
  ignore: 'Ignore',
}

export function UltraadminPatternPipelinePage() {
  const [items, setItems] = useState<PatternPromotionResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('')
  const [stats, setStats] = useState({ total: 0, pending: 0, review_queue: 0, promoted: 0, flagged: 0 })
  const [detailId, setDetailId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await patternPipelineApi.list(filter || undefined, 'date')
      setItems(result.items)
      setStats({
        total: result.total,
        pending: result.pending,
        review_queue: result.review_queue,
        promoted: result.promoted,
        flagged: result.flagged,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to load pattern pipeline')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handlePromote = async (id: string, level: 'regional' | 'global') => {
    setActionLoading(id)
    try {
      await patternPipelineApi.promote(id, level)
      setSuccess(`Pattern promoted to ${level}`)
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to promote pattern')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (id: string) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection')
      return
    }
    setActionLoading(id)
    try {
      await patternPipelineApi.reject(id, rejectReason)
      setSuccess('Pattern rejected')
      setShowRejectModal(false)
      setRejectReason('')
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to reject pattern')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReAnalyze = async (id: string) => {
    setActionLoading(id)
    try {
      await patternPipelineApi.reAnalyze(id)
      setSuccess('Re-analysis queued')
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to re-analyze')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Pattern Confidence Pipeline</h1>
        <p className="mt-1 text-gray-500">Review and manage pattern promotions across all schools</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-sm text-gray-500">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-yellow-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-purple-600">Review Queue</div>
          <div className="text-2xl font-bold text-purple-700">{stats.review_queue}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-green-600">Promoted</div>
          <div className="text-2xl font-bold text-green-700">{stats.promoted}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-orange-600">Flagged</div>
          <div className="text-2xl font-bold text-orange-700">{stats.flagged}</div>
        </Card>
      </div>

      {/* Filter */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filter by status</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
              !filter ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {['pending', 'review_queue', 'flagged', 'promoted_regional', 'promoted_global', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                filter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </Card>

      {/* Pipeline Items */}
      <div className="space-y-4">
        {items.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            No patterns in the pipeline{filter ? ` with status "${filter}"` : ''}
          </Card>
        ) : (
          items.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded truncate">
                      {item.pattern_regex}
                    </code>
                    <Badge variant={item.status === 'promoted_global' ? 'confirmed' : 'default'}>
                      {FIELD_LABELS[item.pattern_field] || item.pattern_field}
                    </Badge>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-800'}`}>
                      {item.status.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {item.stage1_score != null && (
                      <span className="flex items-center gap-1">
                        Stage 1: <span className="font-medium">{(item.stage1_score * 100).toFixed(0)}%</span>
                      </span>
                    )}
                    {item.stage2_score != null && (
                      <span className="flex items-center gap-1">
                        Stage 2: <span className="font-medium">{(item.stage2_score * 100).toFixed(0)}%</span>
                      </span>
                    )}
                    {item.combined_score != null && (
                      <span className="flex items-center gap-1">
                        Combined: <span className="font-medium">{(item.combined_score * 100).toFixed(0)}%</span>
                      </span>
                    )}
                    <span className="text-gray-400">
                      {new Date(item.created_at).toLocaleDateString('de-CH')}
                    </span>
                  </div>

                  {/* Detail view */}
                  {detailId === item.id && item.affected_schools && item.affected_schools.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Regional Analysis</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-1 px-2">School</th>
                              <th className="text-left py-1 px-2">Status</th>
                              <th className="text-right py-1 px-2">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.affected_schools.map((s) => (
                              <tr key={s.school_id} className="border-b hover:bg-gray-50">
                                <td className="py-1 px-2">{s.school_name}</td>
                                <td className="py-1 px-2">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    s.status === 'analyzed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {s.status}
                                  </span>
                                </td>
                                <td className="py-1 px-2 text-right">
                                  {s.score != null ? `${(s.score * 100).toFixed(0)}%` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5 shrink-0">
                  {item.status === 'review_queue' && (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        isLoading={actionLoading === item.id}
                        onClick={() => handlePromote(item.id, item.combined_score != null && item.combined_score >= 0.95 ? 'global' : 'regional')}
                      >
                        <ArrowUpRight className="w-3.5 h-3.5 mr-1" />
                        Promote
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        isLoading={actionLoading === item.id}
                        onClick={() => { setShowRejectModal(true); setDetailId(item.id) }}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {(item.status === 'flagged' || item.status === 'stage2_passed') && (
                    <Button
                      size="sm"
                      variant="secondary"
                      isLoading={actionLoading === item.id}
                      onClick={() => handleReAnalyze(item.id)}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Re-analyze
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDetailId(detailId === item.id ? null : item.id)}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" />
                    {detailId === item.id ? 'Hide' : 'Details'}
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRejectModal(false)}>
          <Card className="p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Pattern</h3>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="danger" isLoading={actionLoading === detailId} onClick={() => detailId && handleReject(detailId)}>
                Reject
              </Button>
              <Button variant="secondary" onClick={() => { setShowRejectModal(false); setRejectReason('') }}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
